// Service Steady PWA — accent color persistence + application.
// v1.0 — 2026-05-04 — Premium polish #9.
//
// Runtime-themable accent color. Defaults to SS-blue #2563EB. Persisted
// per-device in localStorage. Applied by mutating the --ss-accent CSS
// custom property on document.documentElement. Surfaces that opt into
// theming reference var(--ss-accent) directly; brand-locked surfaces (logo,
// theme-color meta) keep the SS-blue Tailwind class.

const STORAGE_KEY = 'ss_accent_v1';

export const SS_DEFAULT_ACCENT = '#2563EB';

export interface AccentPreset {
  /** Storage value — must be a 7-char hex (#rrggbb). */
  hex: string;
  /** Display label shown under the swatch. */
  label: string;
}

export const ACCENT_PRESETS: AccentPreset[] = [
  { hex: '#2563EB', label: 'SS Blue' },
  { hex: '#475569', label: 'Slate' },
  { hex: '#10B981', label: 'Emerald' },
  { hex: '#7C3AED', label: 'Violet' },
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#E11D48', label: 'Rose' },
];

/** Naive luminance check — picks white or near-black foreground for the swatch. */
export function pickForeground(hex: string): '#FFFFFF' | '#0F172A' {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#FFFFFF';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  // Per WCAG-ish: relative luminance threshold at ~0.55 keeps blue/violet/etc
  // showing white text, but flips amber/yellow to dark.
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#0F172A' : '#FFFFFF';
}

export function isValidHex(input: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(input);
}

export function readStoredAccent(): string {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isValidHex(raw)) return raw;
  } catch {
    /* ignore */
  }
  return SS_DEFAULT_ACCENT;
}

export function persistAccent(hex: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, hex);
  } catch {
    /* private mode — accept the loss */
  }
}

/**
 * Apply the given accent to the live document. Safe to call on every render
 * because it's a no-op when the value is unchanged at the CSS-property level.
 */
export function applyAccent(hex: string): void {
  if (typeof document === 'undefined') return;
  const accent = isValidHex(hex) ? hex : SS_DEFAULT_ACCENT;
  document.documentElement.style.setProperty('--ss-accent', accent);
  document.documentElement.style.setProperty('--ss-accent-fg', pickForeground(accent));
}

/**
 * Boot helper — wired from main.tsx so the accent is in place before the
 * first paint, avoiding a flash of SS-blue when the owner has picked another.
 */
export function bootAccent(): void {
  applyAccent(readStoredAccent());
}
